// Browsing back through retained history on the Logs page (TTTL-90).
//
// The live tail (SSE) only ever moves forward; everything older than its
// preload comes from GET /logs. Until the backend has a backward cursor
// the page asks for "the newest N" again with a larger N each time and
// keeps only what it has not seen, so the paging rule lives behind
// `nextHistoryQuery` and swapping it for `before=<cursor>` touches
// nothing else.

import type { LogEntry, LogsQuery, LogsResponse } from "@/types/api";

// Entries carry no id, so identity is the full composite. Identical
// timestamp+level+job+target+message IS the same entry re-delivered — the
// backend's own preload/tail handoff admits duplicates ("at worst
// duplicated in the preload"). The stream dedups on this key, which also
// makes it a stable, unique React key for the rows. Encoded as a JSON
// tuple so a field containing the would-be separator cannot alias
// another entry.
export function entryKey(entry: LogEntry): string {
  return JSON.stringify([
    entry.timestamp,
    entry.level,
    entry.job_name ?? "",
    entry.target,
    entry.message,
  ]);
}

/** Where history browsing stands: idle = older pages may exist and none
 *  is in flight, loading = a page is being fetched, exhausted = the store
 *  has nothing older, truncated = the store stopped at its scan ceiling
 *  with older entries beyond it (asking again returns the same page). */
export type HistoryStatus =
  | "idle"
  | "loading"
  | "failed"
  | "exhausted"
  | "truncated";

/** Whether a new request may start: nothing in flight, and the store has
 *  not said it is done. A failure is retried on the next scroll. */
export function canLoadOlder(status: HistoryStatus): boolean {
  return status === "idle" || status === "failed";
}

/** Entries the stream sends on connect — the whole client window, so a
 *  fresh page already holds as much as it can show. */
export const STREAM_PRELOAD = 1000;

/** Entries each older page adds. */
export const HISTORY_PAGE = 1000;

/** How close to the top (px) still counts as "at the start". */
export const TOP_THRESHOLD_PX = 50;

/** Only the job filter narrows the stream itself; "all" leaves the query
 *  to the backfill size alone. */
export function logStreamQuery(jobFilter: string): {
  initial: number;
  job_name?: string;
} {
  return jobFilter === "all"
    ? { initial: STREAM_PRELOAD }
    : { initial: STREAM_PRELOAD, job_name: jobFilter };
}

/** The request for the next older page, given what is already loaded.
 *
 *  The store keeps filling while the tail is paused, and those arrivals
 *  sit at the top of a "newest N" window, taking the room meant for
 *  older entries. `newerSeen` — how many the last page showed above the
 *  view — widens the window so the page still reaches back. */
export function nextHistoryQuery(
  loadedCount: number,
  jobFilter: string,
  newerSeen = 0,
): LogsQuery {
  const limit = loadedCount + newerSeen + HISTORY_PAGE;
  return jobFilter === "all" ? { limit } : { limit, job_name: jobFilter };
}

/** A newest-N page merged into the view: the view as it should read
 *  afterwards, how many entries that added, and how many entries of the
 *  page arrived after the view.
 *
 *  The merge is by overlap in store order, not by timestamp. The stream
 *  and GET /logs share one order (in HA, XADD order, which interleaves
 *  replicas' entries out of timestamp order), and the view holds a run
 *  of it. So the page reads [older..., the run..., arrivals...]: up to
 *  the last entry the view already holds, the page's order is the truth
 *  — which also fills a gap the run may carry, where a dropped stream
 *  reconnected past more entries than its preload — and everything after
 *  is the live tail's share of the window. The view's own objects stand
 *  in for the entries it already holds (selection is by identity), and
 *  entries it holds that the page no longer does — trimmed from the
 *  store since — stay ahead of it. A page with no overlap is all
 *  arrivals — a burst pushed the run out of the newest N — and the next
 *  window widens by that much. */
export function mergePage(
  loaded: LogEntry[],
  page: LogEntry[],
): { merged: LogEntry[]; added: number; newer: number } {
  if (loaded.length === 0)
    return { merged: page, added: page.length, newer: 0 };
  const held = new Map(loaded.map((entry) => [entryKey(entry), entry]));
  const lastOverlap = page.map((e) => held.has(entryKey(e))).lastIndexOf(true);
  if (lastOverlap === -1)
    return { merged: loaded, added: 0, newer: page.length };
  const span = page
    .slice(0, lastOverlap + 1)
    .map((entry) => held.get(entryKey(entry)) ?? entry);
  const inSpan = new Set(span.map(entryKey));
  const trimmed = loaded.filter((entry) => !inSpan.has(entryKey(entry)));
  const merged = [...trimmed, ...span];
  return {
    merged,
    added: merged.length - loaded.length,
    newer: page.length - lastOverlap - 1,
  };
}

/** Where history stands after a page landed.
 *
 *  The store saying it reached its oldest entry is final. A short page
 *  otherwise means the walk stopped early: at the oldest entry on a store
 *  that cannot say so, or at the scan ceiling (`reached_oldest` false)
 *  with older entries beyond it. A full page that added nothing is the
 *  end too — unless arrivals took the room, in which case the next
 *  request widens and asks again. */
export function pageOutcome(
  added: number,
  response: LogsResponse,
  requestedLimit: number,
  newer = 0,
): HistoryStatus {
  if (response.search?.reached_oldest === true) return "exhausted";
  if (response.logs.length < requestedLimit)
    return response.search ? "truncated" : "exhausted";
  return added === 0 && newer === 0 ? "exhausted" : "idle";
}

/** The scroll geometry the rules below read. A scroll element satisfies
 *  it as is; a snapshot of one copies the three fields. */
export interface ScrollMetrics {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/** A rendered row's box, relative to the viewport's top edge. */
export interface RowBox {
  key: string;
  top: number;
  bottom: number;
}

/** The reader's place: the row under the viewport's top edge and how
 *  far into it they are (negative when the row starts above the edge). */
export interface RowAnchor {
  key: string;
  offset: number;
}

/** The row under the viewport's top edge, with the reader's offset into
 *  it. A merge may insert rows above that row — which moves it — and
 *  below it — which must not — so the place is kept by key, not by how
 *  much taller the list became. */
export function firstVisibleRow(
  rows: RowBox[],
  viewportTop: number,
): RowAnchor | null {
  const row = rows.find((candidate) => candidate.bottom > viewportTop);
  return row ? { key: row.key, offset: row.top - viewportTop } : null;
}

/** The scroll offset that puts a row starting at `rowStart` back at the
 *  reader's offset into it. */
export function restoredScrollTop(rowStart: number, offset: number): number {
  return rowStart - offset;
}

export function isAtTop({ scrollTop }: ScrollMetrics): boolean {
  return scrollTop < TOP_THRESHOLD_PX;
}

/** Whether a scroll event is the reader reaching the top: an upward move
 *  that lands in the top band of a list that overflows. Two look-alikes
 *  are refused. A list that shrinks under a narrower filter clamps the
 *  offset to 0 and fires the same event — but then it no longer
 *  overflows by more than the band, so it is told apart by that rather
 *  than by its height, which also drifts by a few pixels as virtualized
 *  rows measure in. And a move down inside the band is the reader
 *  heading for newer entries, not asking for older ones. */
export function reachedTop(
  current: ScrollMetrics,
  previous: ScrollMetrics,
): boolean {
  if (current.scrollHeight - current.clientHeight < TOP_THRESHOLD_PX)
    return false;
  if (current.scrollTop >= previous.scrollTop) return false;
  return isAtTop(current);
}
